/*
 * "Доповни образ" in the cart drawer.
 *
 * After every cart change (the cp:ladder event) it asks Shopify's product
 * recommendations for the most recently added product — complementary first,
 * curated in Search & Discovery, then Shopify's automatic "related" — and
 * shows three that are not already in the cart and are not the gift tee.
 *
 * When the cart is short of the gift threshold, a card whose price closes the
 * gap says so: "+ футболка в подарунок". It is the honest version of a nudge —
 * true for that card, absent on the others.
 *
 * The mount carries data-skip-subtree-update, so the drawer's own morph after
 * a cart change leaves these cards alone instead of wiping them every time.
 * Requests are cached per product, so reopening the drawer costs nothing.
 */
import { CP_LADDER_EVENT, track, refresh } from '@theme/cp-ladder-ui';

const cache = new Map();
const SHOW = 4;

function request(base, productId, intent) {
  const key = productId + ':' + intent;
  if (cache.has(key)) return cache.get(key);
  const url = base + '?section_id=cp-cart-upsell&product_id=' + productId + '&limit=10&intent=' + intent;
  const job = fetch(url)
    .then(function (r) { return r.ok ? r.text() : ''; })
    .then(function (html) {
      if (!html) return [];
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.prototype.slice.call(doc.querySelectorAll('.cp-upsell__card'));
    })
    .catch(function () { return []; });
  cache.set(key, job);
  return job;
}

function render(mount, cart, ladder) {
  const base = mount.dataset.url;
  const giftId = mount.dataset.giftId;
  const lines = (cart.items || []).filter(function (i) { return !(i.properties && i.properties._gift === 'true'); });

  if (!lines.length || !base) { mount.innerHTML = ''; return; }

  // cart.items is newest first: recommend around what was just chosen.
  const anchor = String(lines[0].product_id);
  const inCart = new Set(lines.map(function (i) { return String(i.product_id); }));
  if (giftId) inCart.add(String(giftId));

  const toNext = ladder && ladder.nextKind === 'gift' ? ladder.toNext : 0;
  const signature = anchor + '|' + Array.from(inCart).sort().join(',') + '|' + toNext;
  if (mount.dataset.sig === signature) return;
  mount.dataset.sig = signature;

  /*
   * "Complete the look" means a different kind of thing. Shopify's "related"
   * pick is similarity-based, so a jacket in the cart brought back three
   * jackets and trousers brought back trousers. The rule, in order:
   *   1. drop what is already in the cart and the gift tee;
   *   2. drop products of a type already in the cart (offers.md: "a second
   *      one in another colour" does not sell);
   *   3. among the rest, the ones whose price alone closes the gap to the
   *      gift come first, then the cheapest — an add-on, not a second
   *      big purchase.
   * Curated pairs from Search & Discovery (intent=complementary) are asked
   * for first and pass the same filter.
   */
  const typesInCart = new Set(lines.map(function (i) { return (i.product_type || '').toLowerCase(); }).filter(Boolean));
  const usable = function (c) { return !inCart.has(c.dataset.productId); };
  const otherType = function (c) { return !typesInCart.has((c.dataset.type || '').toLowerCase()); };
  const rank = function (cards) {
    const fresh = cards.filter(usable);
    const looks = fresh.filter(otherType);
    const pool = looks.length >= SHOW ? looks : looks.concat(fresh.filter(function (c) { return !otherType(c); }));
    return pool.slice().sort(function (a, b) {
      const pa = Number(a.dataset.price);
      const pb = Number(b.dataset.price);
      const ca = toNext > 0 && pa >= toNext ? 0 : 1;
      const cb = toNext > 0 && pb >= toNext ? 0 : 1;
      return ca - cb || pa - pb;
    });
  };

  Promise.all([request(base, anchor, 'complementary'), request(base, anchor, 'related')])
    .then(function (sets) {
      const seen = new Set();
      const merged = sets[0].concat(sets[1]).filter(function (c) {
        if (seen.has(c.dataset.productId)) return false;
        seen.add(c.dataset.productId);
        return true;
      });
      return rank(merged);
    })
    .then(function (cards) {
      // A newer cart may have rendered while this one was in flight.
      if (mount.dataset.sig !== signature) return;
      const pick = cards.slice(0, SHOW);
      if (!pick.length) { mount.innerHTML = ''; return; }

      const box = document.createElement('section');
      box.className = 'cp-upsell';
      box.setAttribute('aria-label', 'Доповни образ');
      box.innerHTML = '<p class="cp-upsell__title">Доповни образ</p><div class="cp-upsell__row"></div>';
      const row = box.querySelector('.cp-upsell__row');

      pick.forEach(function (card) {
        const node = document.importNode(card, true);
        const gift = node.querySelector('[data-cp-upsell-gift]');
        if (gift) gift.hidden = !(toNext > 0 && Number(node.dataset.price) >= toNext);
        row.appendChild(node);
      });

      mount.innerHTML = '';
      mount.appendChild(box);
      track('upsell_shown', { source: 'cart_drawer', count: pick.length });
    });
}

/*
 * On an empty cart the drawer is rendered without the mount at all; it only
 * appears when the drawer re-renders after the first add, which can land a
 * moment after this event. So the latest state is kept and the mount is
 * looked for again for up to two seconds.
 */
let latest = null;

function renderAll(tries) {
  if (!latest) return;
  const mounts = document.querySelectorAll('[data-cp-upsell]');
  mounts.forEach(function (mount) { render(mount, latest.cart, latest.ladder); });
  if (!mounts.length && tries > 0) setTimeout(function () { renderAll(tries - 1); }, 250);
}

document.addEventListener(CP_LADDER_EVENT, function (event) {
  latest = { cart: event.detail.cart, ladder: event.detail.ladder };
  renderAll(8);
});

document.addEventListener('click', function (event) {
  const size = event.target.closest('.cp-upsell__size[data-variant-id]');
  if (!size) return;
  track('upsell_add', { source: 'cart_drawer', item_id: size.dataset.variantId });
});

/*
 * A cart that already had items when the page loaded never fired an event
 * here, so the first time the drawer opens the cart is read once.
 */
document.querySelectorAll('[data-cp-upsell]').forEach(function (mount) {
  const dialog = mount.closest('dialog');
  // On the cart page the mount is on screen from the start: read the cart now.
  if (!dialog) { refresh().catch(function () {}); return; }
  const watch = new MutationObserver(function () {
    if (!dialog.open || mount.dataset.sig) return;
    refresh().catch(function () {});
  });
  watch.observe(dialog, { attributes: true, attributeFilter: ['open'] });
});
